import { UserType } from "@/types/user";
import { hashInput } from "@/utils/functions/security";
import axios from "axios";
import axiosClient from "@/api/axios";
import { parseError, parseWooError } from "@/api/utils";
import type { ErrorType } from "@/api/utils";

const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Post request to AWS Cloudfront require a sha256 hash of the request body
// This is required for AWS Signature Version 4
// The token we send to and from flask is called X-JWT-Token (it includes the Bearer string but we just pass it as is)

// Login returns an access_token and woocommerce_token
// We need the access_token to comm with the flask api (e.g. /products)
// At the moment, the frontend doesn't comm directly with the woocommerce rest api so we don't need the woocommerce_token
// and then we use the cart_token and nonce token to comm with the woocommerce STORE api
export const login = async ({
  username,
  password,
}: {
  username: string;
  password: string;
}) => {
  try {
    const data = {
      username,
      password,
    };
    const SHACODE = hashInput(data);
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      // Required for AWS Signatures
      "X-Amz-Content-Sha256": SHACODE,
    };

    const response = await axios({
      method: "POST",
      url: `${VITE_API_BASE_URL}/auth/login`,
      headers,
      data,
    });
    if (!response) {
      throw new Error("Failed to login");
    }

    const newAccessToken = response.headers["x-jwt-token"];
    const newRefreshToken = response.headers["x-refresh-token"];
    return {
      data: response.data,
      accessToken: newAccessToken,
      refreshToken: newRefreshToken,
    };
  } catch (error: any) {
    if (error.name === "AbortError" || error.code === "ERR_CANCELED") {
      return { status: "aborted" };
    }
    console.log("Login Error:", error);
    return { status: "error", error: parseWooError(error) };
  }
};

export const logout = async () => {
  try {
    const SHACODE = hashInput("");

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      "X-Amz-Content-Sha256": SHACODE,
    };

    const response = await axiosClient({
      method: "POST",
      url: `${VITE_API_BASE_URL}/auth/logout`,
      headers,
    });

    if (!response) {
      throw new Error("Failed to logout");
    }

    return response.data;
  } catch (error: any) {
    if (error.name === "AbortError" || error.code === "ERR_CANCELED") {
      return { status: "aborted" };
    }
    return { status: "error", error: parseError(error as ErrorType) };
  }
};

export const createAccount = async ({ data }: { data: UserType }) => {
  try {
    const SHACODE = hashInput(data);
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      "X-Amz-Content-Sha256": SHACODE,
    };
    const response = await axios({
      method: "POST",
      url: `${VITE_API_BASE_URL}/auth/register`,
      headers,
      data,
    });

    if (!response) {
      throw new Error("Failed to create account");
    }
    return response.data;
  } catch (error: any) {
    console.error("Create Account Error:", error);

    if (error.name === "AbortError" || error.code === "ERR_CANCELED") {
      return { status: "aborted" };
    }

    // Use Woo error parser here
    return {
      status: "error",
      error: parseWooError(error),
    };
  }
};

export const getNewAccessToken = async ({ refreshToken }) => {
  try {
    if (!refreshToken) {
      throw new Error("No refresh token provided");
    }
    const SHACODE = hashInput("");

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      "X-JWT-Token": refreshToken,
      "X-Amz-Content-Sha256": SHACODE,
    };

    const response = await axios({
      method: "POST",
      url: `${VITE_API_BASE_URL}/auth/refresh`,
      headers,
    });

    if (!response) {
      throw new Error("Failed to retrieve refresh token");
    }

    const newAccessToken = response.headers["x-jwt-token"];

    return { accessToken: newAccessToken };
  } catch (error: any) {
    if (error.name === "AbortError" || error.code === "ERR_CANCELED") {
      return { status: "aborted" };
    }
    return { status: "error", error: parseError(error as ErrorType) };
  }
};
